import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useTimezone } from "../../hooks/useTimezone";
import toastError from "../../errors/toastError";
import {
  getReportBreakdown,
  getReportCsat,
  getReportCsatAgents,
  getReportKanban,
  getReportKanbanStuck,
  getReportPending,
  getReportSummary,
  getReportTags,
  getReportTimeseries,
  getReportTransfers,
} from "../../services/reports.api";

// Doc 0447 — datos del tablero «Informes». Las fechas son LOCALES de la empresa:
// el backend las convierte a UTC con su desfase (sp_report_window).
export const PRESETS = ["today", "7d", "30d", "custom"];
const PERIOD_REFRESH_MS = 60 * 1000;
const LIVE_DEBOUNCE_MS = 1500;

export default function useReportsData() {
  const { getNow } = useTimezone();
  const socketManager = useContext(SocketContext);

  const today = getNow().format("YYYY-MM-DD");
  const daysAgo = (n) => getNow().subtract(n, "days").format("YYYY-MM-DD");

  const [filters, setFilters] = useState(() => ({
    preset: "7d",
    from: daysAgo(6),
    to: today,
    queueId: "",
    userId: "",
    whatsappId: "",
  }));
  const [data, setData] = useState({
    summary: null,
    timeseries: [],
    byChannel: [],
    byQueue: [],
    byAgent: [],
    byConnection: [],
    transfers: [],
    tags: [],
    pending: [],
    csat: null,
    csatAgents: [],
    kanban: [],
    kanbanStuck: [],
  });
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const params = useMemo(
    () => ({
      from: filters.from,
      to: filters.to,
      queueId: filters.queueId || undefined,
      userId: filters.userId || undefined,
      whatsappId: filters.whatsappId || undefined,
    }),
    [filters]
  );

  const setPreset = useCallback(
    (preset) => {
      if (preset === "today") setFilters((f) => ({ ...f, preset, from: today, to: today }));
      else if (preset === "7d") setFilters((f) => ({ ...f, preset, from: daysAgo(6), to: today }));
      else if (preset === "30d") setFilters((f) => ({ ...f, preset, from: daysAgo(29), to: today }));
      else setFilters((f) => ({ ...f, preset }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [today]
  );

  const setFilter = useCallback((key, value) => setFilters((f) => ({ ...f, [key]: value })), []);

  const loadPeriod = useCallback(async () => {
    if (!params.from || !params.to || params.from > params.to) return;
    try {
      const period = { from: params.from, to: params.to };
      const [summary, timeseries, byChannel, byQueue, byAgent, byConnection, transfers, tags, csat, csatAgents, kanban, kanbanStuck] = await Promise.all([
        getReportSummary(params),
        getReportTimeseries(params),
        getReportBreakdown({ ...params, dimension: "channel" }),
        getReportBreakdown({ ...params, dimension: "queue" }),
        getReportBreakdown({ ...params, dimension: "agent" }),
        getReportBreakdown({ ...params, dimension: "connection" }),
        getReportTransfers(period),
        getReportTags({ ...period, limit: 15 }),
        getReportCsat(params),
        getReportCsatAgents(params),
        getReportKanban(period),
        getReportKanbanStuck({ limit: 10 }),
      ]);
      if (!mounted.current) return;
      setData((d) => ({
        ...d,
        summary: summary.data,
        timeseries: timeseries.data,
        byChannel: byChannel.data,
        byQueue: byQueue.data,
        byAgent: byAgent.data,
        byConnection: byConnection.data,
        transfers: transfers.data,
        tags: tags.data,
        csat: csat.data,
        csatAgents: csatAgents.data,
        kanban: kanban.data,
        kanbanStuck: kanbanStuck.data,
      }));
    } catch (err) {
      toastError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [params]);

  const loadLive = useCallback(async () => {
    try {
      const [summary, pending] = await Promise.all([
        getReportSummary(params),
        getReportPending({ limit: 20 }),
      ]);
      if (!mounted.current) return;
      setData((d) => ({ ...d, summary: summary.data, pending: pending.data }));
    } catch {
      /* el refresco en vivo no molesta con errores: el siguiente lo reintenta */
    }
  }, [params]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    loadPeriod();
    loadLive();
    const id = setInterval(loadPeriod, PERIOD_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadPeriod, loadLive]);

  // Tiempo real: el mismo evento que mueve los contadores de la bandeja.
  const liveRef = useRef(loadLive);
  useEffect(() => {
    liveRef.current = loadLive;
  }, [loadLive]);
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager?.getSocket?.(companyId);
    if (!socket) return undefined;
    let timer = null;
    const onTicket = () => {
      clearTimeout(timer);
      timer = setTimeout(() => liveRef.current(), LIVE_DEBOUNCE_MS);
    };
    socket.on(`company-${companyId}-ticket`, onTicket);
    return () => {
      clearTimeout(timer);
      socket.off(`company-${companyId}-ticket`, onTicket);
    };
  }, [socketManager]);

  return { filters, setPreset, setFilter, data, loading, reload: loadPeriod };
}
